import type { LocalizedText } from '@fame/shared';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getLocalizedText(
  text: LocalizedText | string | null | undefined,
  lang: 'nl' | 'fr' | 'en' = 'nl'
): string {
  if (!text) return '';
  if (typeof text === 'string') return text;
  return text[lang] || text.nl || text.en || text.fr || '';
}

/**
 * Format a number as currency with Belgian locale formatting.
 * Returns '€ -' for non-numeric or non-finite input instead of '€ NaN'.
 * @param value - The numeric value to format
 * @returns Formatted currency string with € symbol (e.g., "€ 1.500,00")
 */
export function formatCurrency(value: number | string): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(numValue)) return '€ -';
  return `€ ${numValue.toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Safely extract a field value from JSONB data
 * @param data - The JSONB object (currentData or manualOverrides)
 * @param fieldKey - The field key to extract
 * @param fallback - Fallback value if field is missing
 * @returns The field value as a string or the fallback
 */
export function getRegistrationField(
  data: Record<string, unknown> | null | undefined,
  fieldKey: string,
  fallback: string = '-'
): string {
  if (!data) return fallback;
  const value = data[fieldKey];
  if (value == null || value === '') return fallback;
  return String(value);
}

/**
 * Get the effective value for a registration field, with manual overrides taking precedence
 * Empty strings explicitly clear a field; nullish overrides use the original value.
 * @param currentData - The original webhook data
 * @param manualOverrides - Manual overrides by FAME employees
 * @param fieldKey - The field key to extract
 * @param fallback - Fallback value if field is missing
 * @returns Object with effective value and whether it's been overridden
 */
export function getEffectiveRegistrationField(
  currentData: Record<string, unknown> | null | undefined,
  manualOverrides: Record<string, unknown> | null | undefined,
  fieldKey: string,
  fallback: string = '-'
): { value: string; isOverridden: boolean; originalValue: string } {
  const originalValue = getRegistrationField(currentData, fieldKey, fallback);
  const overrideValue = manualOverrides?.[fieldKey];
  const isOverridden = overrideValue != null;

  return {
    value: isOverridden ? String(overrideValue) : originalValue,
    isOverridden,
    originalValue,
  };
}

/**
 * Extract array field from JSONB data
 * @param data - The JSONB object
 * @param fieldKey - The field key to extract
 * @returns Array of string values
 */
export function getRegistrationArrayField(
  data: Record<string, unknown> | null | undefined,
  fieldKey: string
): string[] {
  if (!data) return [];
  const value = data[fieldKey];
  if (Array.isArray(value)) return value.map(String);
  if (value != null && value !== '') return [String(value)];
  return [];
}

/**
 * Combine first and last name from registration data
 * @param currentData - Registration current data
 * @param manualOverrides - Manual overrides
 * @returns Full name or fallback
 */
export function getRegistrationFullName(
  currentData: Record<string, unknown> | null | undefined,
  manualOverrides: Record<string, unknown> | null | undefined,
  fallback: string = '-'
): string {
  const firstName = getEffectiveRegistrationField(currentData, manualOverrides, 'invoice_first_name', '').value;
  const lastName = getEffectiveRegistrationField(currentData, manualOverrides, 'invoice_last_name', '').value;

  if (firstName === '' && lastName === '') return fallback;
  if (firstName === '') return lastName;
  if (lastName === '') return firstName;
  return `${firstName} ${lastName}`;
}

/**
 * Format date and time string
 * @param dateString - ISO date string
 * @returns Formatted date string (dd/MM/yyyy HH:mm)
 */
export function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

/**
 * Format date only (no time)
 * @param dateString - ISO date string
 * @returns Formatted date string (dd/MM/yyyy)
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
}
